/**
 * Draws a capability's condition tree as an indented list, for the detail rails.
 *
 * The root carries its logical operator as a badge, and its direct children sit
 * indented behind a vertical rule. A child that only negates one thing shows a NOT
 * badge against the thing it negates, which reads better than a pre-negated question.
 */
import type { Representation } from "@financial-advisory/data-access/api-types";
import { LogicalOperator } from "@financial-advisory/data-access/api-types";

const nodeText = (node: Representation): string => node.question || node.name;

/**
 * A NOT node with a single child is shown as a NOT badge against that child.
 */
const asNegation = (node: Representation): Representation | null => {
  if (node.logicalOperator !== LogicalOperator.NOT) return null;
  const children = node.subConditions ?? [];
  return children.length === 1 ? children[0] : null;
};

const Badge = ({ label, tone }: { label: string; tone: "root" | "not" }) => (
  <span
    className={`shrink-0 rounded px-1.5 py-px text-[10px] font-bold leading-4 text-white ${
      tone === "root" ? "bg-unlikely" : "bg-brand-muted"
    }`}
  >
    {label}
  </span>
);

interface CapabilityStructureProps {
  root: Representation;
  /**
   * Line shown against the root's operator badge. Defaults to the root's own text.
   */
  rootLabel?: string;
}

export const CapabilityStructure = ({
  root,
  rootLabel,
}: CapabilityStructureProps) => {
  const children = root.subConditions ?? [];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 rounded-[7px] border border-unlikely/20 bg-unlikely/[0.05] px-2.5 py-1.5">
        {root.logicalOperator && (
          <Badge label={root.logicalOperator} tone="root" />
        )}
        <span className="text-[13px] font-medium">
          {rootLabel ?? nodeText(root)}
        </span>
      </div>

      {children.length > 0 && (
        <div className="ml-2 flex flex-col gap-1.5 border-l-2 border-gray-200 pl-3.5">
          {children.map((child, index) => {
            const negated = asNegation(child);
            return (
              <div
                key={`${child.name}-${index}`}
                className="flex items-center gap-2 rounded-[7px] border border-brand-border px-2.5 py-1.5"
              >
                {negated && <Badge label="NOT" tone="not" />}
                <span className="text-[13px]">
                  {nodeText(negated ?? child)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Every question the capability puts to the conversation, in tree order and with
 * repeats removed. These are the same questions that show as known or unknown in
 * the chat panel.
 */
export const capabilityQuestions = (root: Representation): string[] => {
  const seen = new Set<string>();
  const questions: string[] = [];

  const walk = (node: Representation) => {
    const children = node.subConditions ?? [];
    if (children.length === 0) {
      const question = node.question?.trim();
      if (question && !seen.has(question.toLowerCase())) {
        seen.add(question.toLowerCase());
        questions.push(question);
      }
      return;
    }
    children.forEach(walk);
  };

  walk(root);
  return questions;
};

/**
 * How many direct criteria the capability was ingested into.
 */
export const criteriaCount = (root: Representation): number =>
  (root.subConditions ?? []).length;
